<template>
  <div class="recharge-package-page">
    <el-card class="box-card">
      <div class="header-actions">
        <h2 class="page-title">充值中心</h2>
      </div>

      <div class="member-container" v-loading="packagesLoading">
        <!-- 会员状态 -->
        <div class="member-status">
          <h3>
            {{ balanceInfo.vipLevel || '普通用户' }} 
            有效期至: {{ balanceInfo.vipExpireTime || '未开通' }} 
            <template v-if="balanceInfo.vipExpireTime && balanceInfo.vipExpireTime !== '-' && balanceInfo.vipExpireTime !== '未开通'">
              (<span v-if="vipRemainingDays < 0" style="color: #ffcdd2;">已过期</span><span v-else>剩余{{ vipRemainingDays }}天</span>)
            </template>
          </h3>
        </div>

        <!-- 会员特权说明 -->
        <div class="privilege-list">
          <h3>会员特权说明:</h3>
          <div class="privilege-item">✔ 购买的会员权益自动叠加，额度立即生效</div>
          <div class="privilege-item">✔ 会员有效期按最长剩余时长计算(例如:原剩余30天，新购90天，最终为90天)</div>
          <div class="privilege-item">✔ 会员等级升级后，可享受更多高级功能和更大配额</div>
          <div class="privilege-item">✔ 所有查询在会员有效期内可持续使用</div>
        </div>

        <!-- 会员套餐选择 -->
        <div class="plan-container">
          <div v-for="plan in memberPlans" :key="plan.id"
               :class="['plan-card', plan.isCurrent ? 'current' : '', plan.isSpecial ? 'highlight' : '']">
            <div v-if="plan.isSpecial" class="plan-tag">限时特惠</div>
            <div class="plan-name">{{ plan.name }}</div>
            <div class="plan-price">￥{{ plan.price }}</div>
            <div class="plan-duration">{{ plan.duration }}个月有效期</div>

            <div class="plan-features">
              <div class="feature-title">包含功能:</div>
              <div class="feature-item">
                ✓ 会员等级: {{ plan.vipLevel }}
              </div>
              <div class="feature-item">
                ✓ 增加额度: <span class="feature-count">{{ plan.viewRecords }}次</span>
              </div>
              <div class="feature-item" v-if="plan.description">
                ✓ {{ plan.description }}
              </div>
            </div>

            <el-button
                v-if="plan.isCurrent"
                type="info"
                class="downgrade-btn"
                size="small"
                disabled>
              当前套餐
            </el-button>
            <el-button
                v-else
                :type="plan.isSpecial ? 'danger' : 'primary'"
                class="upgrade-btn"
                size="small"
                @click="handleUpgrade(plan)">
              立即购买
            </el-button>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 套餐确认对话框 -->
    <el-dialog
      title="升级会员"
      :visible.sync="upgradeDialogVisible"
      width="450px"
      center>
      <div class="upgrade-dialog-content" v-if="selectedPlan">
        <div class="dialog-icon">
          <i class="el-icon-info" style="font-size: 24px; color: #409EFF;"></i>
        </div>
        <div class="dialog-message">
          <p style="font-size: 16px; margin: 10px 0;">
            确定要升级到<strong>{{ selectedPlan.name }}</strong>吗？
          </p>
          <div class="plan-info">
            <p><strong>价格：</strong>{{ selectedPlan.price }}元</p>
            <p><strong>时长：</strong>{{ selectedPlan.duration }}个月</p>
            <p><strong>包含：</strong>查看{{ selectedPlan.viewRecords }}条联系信息</p>
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="upgradeDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="confirmUpgrade">确认升级</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import request from "@/utils/request";

export default {
  name: 'RechargePackageView',
  data() {
    return {
      packagesLoading: false,
      balanceInfo: {},
      vipRemainingDays: 0,
      memberPlans: [],
      upgradeDialogVisible: false,
      selectedPlan: null
    };
  },
  created() {
    this.loadBalanceInfo();
    this.loadVipPackages();
  },
  methods: {

    // 加载账户余额信息
    async loadBalanceInfo() {
      try {
        const res = await request.get('/balance/mine');
        if (res.code === '200') {
          this.balanceInfo = {
            id: res.data.userId,
            username: res.data.userName,
            phone: res.data.phone || '-',
            vipLevel: res.data.vipName || '普通用户',
            vipExpireTime: res.data.vipExpireTime || null,
            remainingCount: res.data.viewRecordsRemaining || 0,
            createTime: res.data.createdAt || '-',
            updateTime: res.data.updatedAt || '-'
          };
          this.calculateVipRemainingDays();
        }
      } catch (error) {
        this.$message.error('获取账户信息失败');
      }
    },

    // 加载VIP套餐列表
    async loadVipPackages() {
      this.packagesLoading = true;
      try {
        const res = await request.get('/vip/packages/list');
        if (res.code == 200) {
          this.memberPlans = (res.data || []).map(item => ({
            ...item,
            isCurrent: false,
            isSpecial: item.isSpecial || false
          }));
        } else {
          this.$message.error(res.msg || '获取套餐列表失败');
        }
      } catch (error) {
        this.$message.error('获取套餐列表失败');
      } finally {
        this.packagesLoading = false;
      }
    },

    // 计算VIP剩余天数
    calculateVipRemainingDays() {
      if (this.balanceInfo.vipExpireTime && this.balanceInfo.vipExpireTime !== '-') {
        const expireDate = new Date(this.balanceInfo.vipExpireTime);
        const today = new Date();
        const diffTime = expireDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        this.vipRemainingDays = diffDays;
      } else {
        this.vipRemainingDays = 0;
      }
    },

    // 处理套餐升级
    handleUpgrade(plan) {
      this.selectedPlan = plan;
      this.upgradeDialogVisible = true;
    },

    // 确认升级
    confirmUpgrade() {
      this.upgradeDialogVisible = false;
      
      // 跳转到充值页面，并传递套餐信息
      this.$router.push({
        path: '/recharge',
        query: {
          packageId: this.selectedPlan.id,
          packageName: this.selectedPlan.name,
          amount: this.selectedPlan.price,
          remark: `购买${this.selectedPlan.name} ${this.selectedPlan.duration}个月有效期`
        }
      });
    }
  }
}
</script>

<style scoped>
/* ========== 页面容器 ========== */
.recharge-package-page {
  padding: 40px 30px;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf2 100%);
  min-height: 100vh;
  max-width: 1400px;
  margin: 0 auto;
}

.header-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.box-card {
  border: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  padding: 30px;
}

/* ========== 充值中心 ========== */
.member-container {
  padding: var(--spacing-xl);
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf2 100%);
  min-height: 500px;
  border-radius: var(--radius-md);
}

.member-status {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 25px;
  border-radius: var(--radius-lg);
  margin-bottom: var(--spacing-lg);
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.3);
}

.member-status h3 {
  color: white;
  font-size: 18px;
  font-weight: 700;
  margin: 0;
}

.member-status h3::before {
  content: '👑';
  margin-right: 10px;
}

.member-status h3 span[style*="color: #ffcdd2"] {
  font-weight: 700;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  animation: blink 2s infinite;
}

@keyframes blink {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.privilege-list {
  margin-bottom: 30px;
  padding: 25px;
  background: white;
  border-radius: var(--radius-lg);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
}

.privilege-list h3 {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 20px;
}

.privilege-list h3::before {
  content: '✨';
  margin-right: 10px;
}

.privilege-item {
  margin-bottom: 12px;
  color: var(--text-secondary);
  font-size: 15px;
  padding: 10px 15px;
  background: rgba(102, 126, 234, 0.03);
  border-radius: var(--radius-md);
  border-left: 3px solid var(--primary-color);
  transition: all 0.3s;
}

.privilege-item:hover {
  background: rgba(102, 126, 234, 0.08);
  transform: translateX(5px);
}

.plan-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 24px;
  justify-content: center;
}

.plan-card {
  background: white;
  border: 2px solid #e4e7ed;
  border-radius: var(--radius-lg);
  padding: 30px 25px;
  text-align: center;
  position: relative;
  transition: all 0.3s;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
}

.plan-card:hover {
  border-color: var(--primary-color);
  box-shadow: 0 8px 28px rgba(102, 126, 234, 0.2);
  transform: translateY(-8px);
}

.plan-card.current {
  border-color: #67c23a;
  background: linear-gradient(135deg, rgba(103, 194, 58, 0.05) 0%, rgba(103, 194, 58, 0.02) 100%);
  box-shadow: 0 4px 16px rgba(103, 194, 58, 0.2);
}

.plan-card.highlight {
  border-color: #f56c6c;
  background: linear-gradient(135deg, rgba(245, 108, 108, 0.05) 0%, rgba(245, 108, 108, 0.02) 100%);
  box-shadow: 0 4px 16px rgba(245, 108, 108, 0.2);
}

.plan-tag {
  position: absolute;
  top: -12px;
  right: 20px;
  background: linear-gradient(135deg, #ff6b6b 0%, #ff8e53 100%);
  color: white;
  padding: 4px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 700;
  box-shadow: 0 4px 12px rgba(245, 108, 108, 0.4);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

.plan-name {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 15px;
  color: var(--text-primary);
}

.plan-price {
  font-size: 36px;
  font-weight: 700;
  background: var(--primary-gradient);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 8px;
}

.plan-duration {
  color: var(--text-tertiary);
  margin-bottom: 20px;
  font-size: 14px;
}

.plan-features {
  text-align: left;
  margin: 20px 0;
  min-height: 150px;
  padding: 20px;
  background: rgba(102, 126, 234, 0.03);
  border-radius: var(--radius-md);
}

.feature-title {
  font-weight: 700;
  margin-bottom: 15px;
  text-align: center;
  color: var(--text-primary);
  font-size: 15px;
}

.feature-item {
  margin-bottom: 10px;
  font-size: 14px;
  color: var(--text-secondary);
  padding-left: 5px;
}

.feature-count {
  color: var(--primary-color);
  font-weight: 700;
  font-size: 16px;
}

.upgrade-btn, .downgrade-btn {
  width: 100%;
  height: 42px;
  border-radius: 21px;
  font-weight: 700;
  font-size: 15px;
  transition: all 0.3s;
}

.upgrade-btn {
  background: var(--primary-gradient);
  border: none;
  color: white;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.upgrade-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.plan-card.highlight .upgrade-btn {
  background: linear-gradient(135deg, #ff6b6b 0%, #ff8e53 100%);
  box-shadow: 0 4px 12px rgba(245, 108, 108, 0.3);
}

.plan-card.highlight .upgrade-btn:hover {
  box-shadow: 0 6px 20px rgba(245, 108, 108, 0.4);
}

.downgrade-btn {
  background: #e4e7ed;
  color: var(--text-tertiary);
  border: none;
  cursor: not-allowed;
}

/* ========== 升级对话框 ========== */
.upgrade-dialog-content {
  text-align: center;
  padding: 30px 20px;
}

.dialog-icon {
  margin-bottom: 20px;
}

.dialog-icon i {
  width: 60px;
  height: 60px;
  line-height: 60px;
  border-radius: 50%;
  background: rgba(102, 126, 234, 0.1);
  color: var(--primary-color);
}

.dialog-message {
  color: var(--text-secondary);
}

.dialog-message p {
  font-size: 16px;
  line-height: 1.6;
  color: var(--text-primary);
}

.plan-info {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%);
  padding: 20px;
  border-radius: var(--radius-md);
  margin-top: 20px;
  text-align: left;
  border: 2px solid rgba(102, 126, 234, 0.2);
}

.plan-info p {
  margin: 10px 0;
  line-height: 1.8;
  color: var(--text-primary);
  font-size: 15px;
}

.plan-info strong {
  color: var(--primary-color);
}

/* ========== 响应式设计 ========== */
@media (max-width: 768px) {
  .recharge-package-page {
    padding: var(--spacing-md);
  }

  .member-container {
    padding: var(--spacing-md);
  }

  .plan-container {
    grid-template-columns: 1fr;
  }
}

/* ========== 动画效果 ========== */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.member-container {
  animation: fadeInUp 0.6s ease-out;
}

.plan-card {
  animation: fadeInUp 0.6s ease-out;
}

.plan-card:nth-child(1) { animation-delay: 0.1s; }
.plan-card:nth-child(2) { animation-delay: 0.2s; }
.plan-card:nth-child(3) { animation-delay: 0.3s; }
.plan-card:nth-child(4) { animation-delay: 0.4s; }
</style>
